import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { queryKeys } from '@/lib/queryKeys';
import { invalidateEquipe, invalidateAffectationsCalendrier } from '@/lib/queryInvalidation';
import { calculateJoursOuvres } from '@/lib/heures-utils';
import {
  BulkAbsenceConflict,
  BulkAffectationConflict,
  MembreDisponibilite,
  BulkDisponibiliteResult,
} from '@/types/conflits-absence';

// ============================================================
// TYPES
// ============================================================

interface BulkAffectationParams {
  membreIds: string[];
  chantierId: string;
  dateDebut: string;
  dateFin: string;
  heuresParJour: number;
}

interface BulkAffectationResult {
  success: boolean;
  created: number;
  failed: number;
  errors: { membreId: string; error: string }[];
}

interface BulkConflitsDetails {
  absences: BulkAbsenceConflict[];
  affectations: BulkAffectationConflict[];
}

// ============================================================
// HOOK — Migré vers React Query (useMutation)
// ============================================================

/**
 * Hook pour gérer l'affectation de plusieurs membres à un chantier
 * Utilise les fonctions RPC bulk pour la vérification des conflits
 */
export function useAffectationChantierBulk() {
  const queryClient = useQueryClient();
  const { entrepriseId } = useSubscription();

  // ─── Mutation 1 : vérifier la disponibilité bulk ───
  const checkMutation = useMutation({
    mutationFn: async ({ membreIds, dateDebut, dateFin }: {
      membreIds: string[];
      dateDebut: string;
      dateFin: string;
    }): Promise<BulkDisponibiliteResult> => {
      const { data, error } = await supabase.rpc('get_membres_disponibilite_bulk', {
        p_membre_ids: membreIds,
        p_date_debut: dateDebut,
        p_date_fin: dateFin,
      });

      if (error) {
        console.error('Erreur vérification disponibilité bulk:', error);
        throw new Error('Impossible de vérifier la disponibilité');
      }

      const disponibilites: MembreDisponibilite[] = (data || []).map((d: Record<string, unknown>) => ({
        membre_id: d.membre_id as string,
        a_absence: d.a_absence as boolean,
        a_affectation: d.a_affectation as boolean,
        total_heures_jour: Number(d.total_heures_jour) || 0,
        est_disponible: d.est_disponible as boolean,
      }));

      return {
        disponibilites,
        membres_disponibles: disponibilites.filter((d) => d.est_disponible).map((d) => d.membre_id),
        membres_indisponibles: disponibilites.filter((d) => !d.est_disponible).map((d) => d.membre_id),
      };
    },
  });

  // ─── Mutation 2 : récupérer les détails des conflits ───
  const conflitsMutation = useMutation({
    mutationFn: async ({ membreIds, dateDebut, dateFin }: {
      membreIds: string[];
      dateDebut: string;
      dateFin: string;
    }): Promise<BulkConflitsDetails> => {
      // Appeler les 2 fonctions RPC en parallèle
      const [absencesRes, affectationsRes] = await Promise.all([
        supabase.rpc('check_membres_absences_bulk', {
          p_membre_ids: membreIds,
          p_date_debut: dateDebut,
          p_date_fin: dateFin,
        }),
        supabase.rpc('check_membres_affectations_bulk', {
          p_membre_ids: membreIds,
          p_date_debut: dateDebut,
          p_date_fin: dateFin,
        }),
      ]);

      if (absencesRes.error) {
        console.error('Erreur vérification absences bulk:', absencesRes.error);
      }
      if (affectationsRes.error) {
        console.error('Erreur vérification affectations bulk:', affectationsRes.error);
      }

      const absences: BulkAbsenceConflict[] = (absencesRes.data || []).map((a: Record<string, unknown>) => ({
        membre_id: a.membre_id as string,
        absence_id: a.absence_id as string,
        type_absence: a.type_absence as string,
        date_debut: a.date_debut as string,
        date_fin: a.date_fin as string,
        jours_conflit: Number(a.jours_conflit) || 0,
        motif: a.motif as string | null,
      }));

      const affectations: BulkAffectationConflict[] = (affectationsRes.data || []).map((a: Record<string, unknown>) => ({
        membre_id: a.membre_id as string,
        affectation_id: a.affectation_id as string,
        chantier_id: a.chantier_id as string,
        chantier_nom: a.chantier_nom as string,
        chantier_reference: a.chantier_reference as string | null,
        date_debut: a.date_debut as string,
        date_fin: a.date_fin as string,
        jours_conflit: Number(a.jours_conflit) || 0,
        heures_par_jour: a.heures_par_jour != null ? Number(a.heures_par_jour) : null,
      }));

      return { absences, affectations };
    },
  });

  // ─── Mutation 3 : affecter les membres au chantier ───
  const affectMutation = useMutation({
    mutationFn: async ({
      membreIds,
      chantierId,
      dateDebut,
      dateFin,
      heuresParJour,
    }: BulkAffectationParams): Promise<BulkAffectationResult> => {
      const result: BulkAffectationResult = {
        success: false,
        created: 0,
        failed: 0,
        errors: [],
      };

      if (membreIds.length === 0) {
        throw new Error('Aucun membre sélectionné');
      }

      // 1. Récupérer les infos de tous les membres (taux_horaire, charges_patronales)
      const { data: membres, error: membresError } = await supabase
        .from('membres_equipe')
        .select('id, taux_horaire, charges_patronales')
        .in('id', membreIds);

      if (membresError) throw membresError;
      if (!membres || membres.length === 0) {
        throw new Error('Membres non trouvés');
      }

      // 2. Calculer les jours ouvrés
      const joursOuvres = calculateJoursOuvres(dateDebut, dateFin);

      // 3. Préparer les rows pour l'INSERT
      const rows = membres.map((m) => ({
        membre_equipe_id: m.id,
        chantier_id: chantierId,
        date_debut: dateDebut,
        date_fin: dateFin,
        jours_travailles: joursOuvres,
        heures_par_jour: heuresParJour,
        taux_horaire_specifique: m.taux_horaire,
        charges_patronales_pct: m.charges_patronales,
      }));

      // 4. INSERT multi-rows
      // Le trigger anti-doublon protège contre les doublons
      const { data: inserted, error: insertError } = await supabase
        .from('affectations_chantiers')
        .insert(rows)
        .select('id, membre_equipe_id');

      if (insertError) {
        // Si erreur (ex: doublon), on essaie un par un pour savoir lesquels échouent
        console.warn('Erreur INSERT batch, tentative individuelle:', insertError);

        for (const row of rows) {
          try {
            const { error: singleError } = await supabase
              .from('affectations_chantiers')
              .insert(row);

            if (singleError) {
              result.failed++;
              result.errors.push({
                membreId: row.membre_equipe_id,
                error: singleError.message || 'Erreur création affectation',
              });
            } else {
              result.created++;
            }
          } catch (err) {
            result.failed++;
            result.errors.push({
              membreId: row.membre_equipe_id,
              error: err instanceof Error ? err.message : 'Erreur inconnue',
            });
          }
        }
      } else {
        // Succès total
        result.created = inserted?.length || rows.length;
      }

      result.success = result.created > 0;
      return result;
    },
    onSuccess: (_data, variables) => {
      if (entrepriseId) {
        invalidateEquipe(queryClient, entrepriseId);
        queryClient.invalidateQueries({
          queryKey: queryKeys.affectations.chantier(entrepriseId, variables.chantierId),
        });
        invalidateAffectationsCalendrier(queryClient, entrepriseId);
      }
    },
  });

  // ─── Wrappers rétrocompatibles ───

  const checkDisponibiliteBulk = async (
    membreIds: string[],
    dateDebut: string,
    dateFin: string
  ): Promise<BulkDisponibiliteResult> => {
    if (membreIds.length === 0) {
      return { disponibilites: [], membres_disponibles: [], membres_indisponibles: [] };
    }
    return checkMutation.mutateAsync({ membreIds, dateDebut, dateFin });
  };

  const getConflitsDetails = async (
    membreIds: string[],
    dateDebut: string,
    dateFin: string
  ): Promise<BulkConflitsDetails> => {
    if (membreIds.length === 0) {
      return { absences: [], affectations: [] };
    }
    return conflitsMutation.mutateAsync({ membreIds, dateDebut, dateFin });
  };

  const affecterMembresAuChantier = async (params: BulkAffectationParams): Promise<BulkAffectationResult> => {
    try {
      return await affectMutation.mutateAsync(params);
    } catch (err) {
      // Rétrocompatibilité : l'ancien code ne rejetait jamais, il retournait toujours un résultat
      const e = err instanceof Error ? err : new Error("Erreur lors de l'affectation en masse");
      return {
        success: false,
        created: 0,
        failed: 0,
        errors: [{ membreId: 'global', error: e.message }],
      };
    }
  };

  return {
    checkDisponibiliteBulk,
    getConflitsDetails,
    affecterMembresAuChantier,
    isLoading: affectMutation.isPending,
    error: affectMutation.error instanceof Error ? affectMutation.error : null,
  };
}
